/**
 * Radio Component
 * A reusable radio button component following the design pattern from Reward Catalog.
 */

import { forwardRef, type InputHTMLAttributes } from "react";

export type RadioVariant = "default" | "card";

export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** The label text displayed next to the radio button */
  readonly label: string;
  /** Whether the radio button has an error state */
  readonly hasError?: boolean;
  /** Visual variant: 'default' for simple radio, 'card' for bordered card style */
  readonly variant?: RadioVariant;
}

/**
 * Radio component with custom styling matching the Reward Catalog design.
 * Uses a filled circle effect when checked (border-[5px] technique).
 *
 * Variants:
 * - default: Simple radio with label
 * - card: Radio inside a bordered card container
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    { label, hasError = false, variant = "default", className, disabled, checked, ...props },
    ref
  ): React.JSX.Element => {
    const inputElement = (
      <input
        ref={ref}
        type="radio"
        disabled={disabled}
        checked={checked}
        className={`
          appearance-none w-4 h-4 border-2 rounded-full cursor-pointer transition-all
          ${hasError
            ? "border-red-500 checked:border-red-500"
            : "border-gray-300 checked:border-[#001a41]"
          }
          checked:border-[5px]
          ${disabled ? "cursor-not-allowed" : ""}
          focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#001a41]/20
        `}
        {...props}
      />
    );

    if (variant === "card") {
      return (
        <label
          className={`
            flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-all
            ${checked
              ? "border-[#001A41] bg-white"
              : "border-gray-300 bg-white hover:border-gray-400"
            }
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}
            ${hasError ? "border-red-500" : ""}
            ${className ?? ""}
          `}
        >
          <div className="relative flex items-center justify-center">
            {inputElement}
          </div>
          <span className={`text-sm ${hasError ? "text-red-500" : "text-gray-800"}`}>
            {label}
          </span>
        </label>
      );
    }

    // Default variant
    return (
      <label
        className={`
          flex items-center gap-2 cursor-pointer text-sm text-gray-800
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
          ${className ?? ""}
        `}
      >
        <div className="relative flex items-center justify-center">
          {inputElement}
        </div>
        <span className={hasError ? "text-red-500" : ""}>{label}</span>
      </label>
    );
  }
);

Radio.displayName = "Radio";

